/**
 * <StudentValidateEmailPage> — « Valider l'email » (page).
 *
 * L'ancien `ValidateEmailScreen` (et sa carte, `ValidateEmailCard`), geste pour geste : l'adresse à
 * valider, la modifier, renvoyer l'email de validation, et « Actualiser » une fois le lien cliqué —
 * l'écran se ferme seul dès que le compte est validé (c'est l'écran qui le sait).
 *
 * Contrôlée : l'envoi en cours, la relecture en cours et l'avis d'un geste (« Email renvoyé. », un
 * refus) arrivent en props.
 *
 * Composes DS pieces only — `<PageTemplate>`, `<ClosableHeader>`, `<Card>`, `<ChampionAvatar>`,
 * `<Title>`, `<Text>`, `<TextButton>`, `<Button>`, `<Snackbar>`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../../molecules/Card"
import { ClosableHeader } from "../../molecules/ClosableHeader"
import { Snackbar, type SnackbarProps } from "../../molecules/Snackbar"
import { PageTemplate } from "../../templates/PageTemplate"

export type StudentValidateEmailNotice = Pick<SnackbarProps, "message" | "tone">

export type StudentValidateEmailPageProps = {
    /** L'adresse à valider. `null` tant que le compte n'est pas là. */
    email: string | null
    /** La croix de l'en-tête. */
    onClose: () => void
    /** « Modifier » : l'écran de changement d'adresse. */
    onEditEmail: () => void
    /** « Renvoyer l'email ». */
    onResend: () => void
    /** L'email part : le lien tourne et ne répond plus. Défaut `false`. */
    resending?: boolean
    /** « Actualiser » : relire le compte, une fois le lien cliqué. */
    onCheck: () => void
    /** La relecture est en cours : le bouton tourne. Défaut `false`. */
    checking?: boolean
    /** L'issue d'un geste — « Email renvoyé. », ou un refus. */
    notice?: StudentValidateEmailNotice | null
    onDismissNotice?: () => void
    testID?: string
}

const TEXTS = {
    title: "Valider l'email",
    heading: "Valide ton adresse email",
    explain: "Clique sur le lien qu'on t'a envoyé par email à cette adresse :",
    edit: "Modifier l'adresse",
    notReceived: "Tu n'as pas reçu d'email ?",
    resend: "Renvoyer l'email",
    check: "Actualiser",
    checkHint: "Tu as cliqué sur le lien ? Actualise pour continuer.",
}

/** La colonne d'une page de lecture courte : une carte qui se lit d'un regard. */
const CONTENT_WIDTH = 560
/** La tête de Tina, à la taille moyenne du livre (72 · 54 · 44 · 34) — l'ancien écran la montrait. */
const CHAMPION_SIZE = 72

export function StudentValidateEmailPage(props: StudentValidateEmailPageProps) {
    const {
        email,
        onClose,
        onEditEmail,
        onResend,
        resending = false,
        onCheck,
        checking = false,
        notice,
        onDismissNotice,
        testID,
    } = props

    return (
        <PageTemplate
            testID={testID}
            header={<ClosableHeader title={TEXTS.title} onClose={onClose} />}
            padding="4"
            maxContentWidth={CONTENT_WIDTH}
        >
            <Stack gap="4" style={styles.column}>
                <Card p="5">
                    <Stack gap="3" align="center">
                        <ChampionAvatar name="tina" size={CHAMPION_SIZE} />
                        <Title center color="text-strong">
                            {TEXTS.heading}
                        </Title>
                        <Text size="text-md" color="text-muted" center>
                            {TEXTS.explain}
                        </Text>
                        {email != null && email !== "" && (
                            <Text size="text-lg" weight="semibold" color="text-strong" center selectable>
                                {email}
                            </Text>
                        )}
                        <TextButton label={TEXTS.edit} onPress={onEditEmail} />
                    </Stack>
                </Card>

                <Stack gap="1" align="center">
                    <Text size="text-md" color="text-muted" center>
                        {TEXTS.notReceived}
                    </Text>
                    <TextButton label={TEXTS.resend} onPress={onResend} loading={resending} />
                </Stack>

                <Stack gap="2" align="center">
                    <Button
                        label={TEXTS.check}
                        iconName="redo"
                        variant="outline"
                        onPress={onCheck}
                        loading={checking}
                        fullWidth
                    />
                    <Text size="text-sm" color="text-muted" center>
                        {TEXTS.checkHint}
                    </Text>
                </Stack>
            </Stack>

            <Snackbar
                visible={notice != null}
                message={notice?.message ?? ""}
                tone={notice?.tone ?? "success"}
                onDismiss={onDismissNotice ?? NOOP}
            />
        </PageTemplate>
    )
}

/** Stable d'un rendu à l'autre : une fonction neuve relancerait le minuteur de retrait du `<Snackbar>`. */
const NOOP = () => {}

const styles = StyleSheet.create(() => ({
    /** La largeur de la colonne appartient à la page (`PageTemplate maxContentWidth`). */
    column: { width: "100%" },
}))
